import React, { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Card, Empty, Flex, Spin, Table, Typography } from "antd"
import { CloseOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import styles from "./index.module.css"

const EquipmentTable = ({ equipment, onAction, actionType, emptyMessage, canEdit, loading }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const { Text } = Typography

  const renderActionButton = useCallback(
    (item) => (
      <Button
        type="text"
        icon={actionType === "unassign" ? <CloseOutlined /> : <PlusOutlined />}
        onClick={() => onAction(item)}
        loading={loading}
        disabled={loading}
        danger={actionType === "unassign"}
      />
    ),
    [actionType, loading, onAction]
  )

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: <Text className={styles.headerText}>{t("booking.equipment.equipment")}</Text>,
        dataIndex: "name",
        key: "name",
        render: (value) => <Text>{value || "-"}</Text>
      },
      {
        title: <Text className={styles.headerText}>{t("booking.equipment.asset_number")}</Text>,
        dataIndex: "assetNumber",
        key: "assetNumber",
        render: (value) => <Text>{value || "-"}</Text>
      },
      {
        title: <Text className={styles.headerText}>{t("booking.equipment.max_guests")}</Text>,
        dataIndex: "maxGuests",
        key: "maxGuests",
        render: (value) => <Text>{value ?? "-"}</Text>
      },
      {
        title: <Text className={styles.headerText}>{t("booking.equipment.category")}</Text>,
        dataIndex: "category",
        key: "category",
        render: (value) => <Text>{value || "-"}</Text>
      }
    ]

    if (!canEdit) {
      return baseColumns
    }

    return [
      ...baseColumns,
      {
        title: <Text className={styles.headerText}>{t(`booking.equipment.${actionType}`)}</Text>,
        key: "action",
        width: 100,
        align: "center",
        render: (_, item) => renderActionButton(item)
      }
    ]
  }, [actionType, canEdit, renderActionButton, t])

  if (isMobile) {
    if (loading) {
      return (
        <Flex align="center" justify="center" className={styles.mobileState}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
        </Flex>
      )
    }

    if (equipment.length === 0) {
      return <Empty description={emptyMessage} />
    }

    return (
      <Flex vertical gap={12}>
        {equipment.map((item) => (
          <Card key={item.id} size="small">
            <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
              <Text strong>{item.name || "-"}</Text>
              {canEdit && renderActionButton(item)}
            </Flex>

            <Flex vertical gap={8}>
              <Flex justify="space-between" align="center" gap={12}>
                <Text type="secondary">{t("booking.equipment.asset_number")}</Text>
                <Text>{item.assetNumber || "-"}</Text>
              </Flex>
              <Flex justify="space-between" align="center" gap={12}>
                <Text type="secondary">{t("booking.equipment.max_guests")}</Text>
                <Text>{item.maxGuests ?? "-"}</Text>
              </Flex>
              <Flex justify="space-between" align="center" gap={12}>
                <Text type="secondary">{t("booking.equipment.category")}</Text>
                <Text>{item.category || "-"}</Text>
              </Flex>
            </Flex>
          </Card>
        ))}
      </Flex>
    )
  }

  return (
    <div className={styles.tableContainer}>
      <Table
        columns={columns}
        dataSource={equipment}
        rowKey="id"
        pagination={false}
        loading={loading}
        locale={{ emptyText: <Text>{emptyMessage}</Text> }}
        className={styles.equipmentTable}
        scroll={{ x: "max-content" }}
      />
    </div>
  )
}

EquipmentTable.propTypes = {
  equipment: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      assetNumber: PropTypes.string.isRequired,
      maxGuests: PropTypes.number,
      category: PropTypes.string.isRequired
    })
  ).isRequired,
  onAction: PropTypes.func.isRequired,
  actionType: PropTypes.oneOf(["assign", "unassign"]).isRequired,
  emptyMessage: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  loading: PropTypes.bool
}

export default EquipmentTable
